/**
 * TemplatesSection (TMPL-01) — Settings → Templates: every outbound document
 * Pegasus can send, in one place. Transactional (statement, invoice email)
 * and the 13 recreated ESC letters (the marketing seed library). Browse +
 * live preview + archive; EDITING arrives with the TMPL-02 visual editor —
 * this surface is its future home, not a parallel one.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, FileText, Mail, Pencil, Plus } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { ReplyTemplatesSection } from "./ReplyTemplatesSection";

interface TemplateRow {
  id: string;
  key: string;
  name: string;
  kind: "transactional" | "marketing";
  subject: string | null;
  html: string;
  version: number;
  archived: boolean;
  updated_by: string | null;
  updated_at: string;
}

function useTemplates() {
  return useQuery({
    queryKey: ["pegasus_templates"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TemplateRow[]> => {
      const { data, error } = await supabase
        .from("pegasus_template")
        .select("id, key, name, kind, subject, html, version, archived, updated_by, updated_at")
        .order("kind", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TemplateRow[];
    },
  });
}

export function TemplatesSection() {
  // Two kinds of template live here: the Inbox quick-replies (reply_template,
  // fully editable) and the marketing emails / letters (the drag-drop builder).
  // ?view=emails opens the marketing side (the Email Builder's back link, settings search).
  const [params] = useSearchParams();
  const [view, setView] = useState<"reply" | "emails">(() => (params.get("view") === "emails" ? "emails" : "reply"));
  return (
    <div className="space-y-4">
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-hairline bg-white">
        {(
          [
            ["reply", "Inbox Templates"],
            ["emails", "Marketing Templates"],
          ] as const
        ).map(([k, label], i) => (
          <button
            key={k}
            type="button"
            onClick={() => setView(k)}
            className={cn(
              "px-3 py-1.5 text-xs font-semibold transition-colors",
              i > 0 && "border-l border-hairline",
              view === k ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {view === "reply" ? <ReplyTemplatesSection /> : <MarketingTemplatesView />}
    </div>
  );
}

function MarketingTemplatesView() {
  const canManage = useCan("settings.manage");
  const { data, isLoading } = useTemplates();
  const qc = useQueryClient();
  const all = data ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const visible = useMemo(
    () => all.filter((t) => showArchived || !t.archived),
    [all, showArchived],
  );
  const selected =
    visible.find((t) => t.key === selectedKey) ?? visible[0] ?? null;

  const setArchived = useMutation({
    mutationFn: async (v: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("pegasus_template")
        .update({ archived: v.archived, updated_at: new Date().toISOString() })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pegasus_templates"] }),
  });

  const groups: { label: string; kind: TemplateRow["kind"] }[] = [
    { label: "Transactional", kind: "transactional" },
    { label: "Letters & Marketing", kind: "marketing" },
  ];

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-xl bg-slate-100" />;
  }

  return (
    <div className="flex items-start gap-4">
      {/* Rail */}
      <div className="w-64 shrink-0 space-y-4">
        {groups.map((g) => {
          const items = visible.filter((t) => t.kind === g.kind);
          if (items.length === 0) return null;
          return (
            <div key={g.kind}>
              <h4 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {g.label}
              </h4>
              <div className="space-y-0.5">
                {items.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSelectedKey(t.key)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] transition-colors",
                      selected?.key === t.key
                        ? "bg-brand-50 font-semibold text-brand-800"
                        : "font-medium text-slate-600 hover:bg-slate-100",
                      t.archived && "opacity-50",
                    )}
                  >
                    {t.kind === "transactional" ? (
                      <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    ) : (
                      <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    )}
                    <span className="truncate">{t.name}</span>
                    {t.archived && (
                      <span className="ml-auto rounded bg-slate-100 px-1 text-[10px] font-bold uppercase text-slate-400">
                        archived
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <label className="flex cursor-pointer items-center gap-1.5 px-2 text-[11px] text-slate-400">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3 w-3 accent-brand-700"
          />
          Show archived
        </label>
        {canManage && (
          <Link
            to="/email-builder"
            className="mx-2 flex items-center gap-1.5 rounded-lg border border-dashed border-brand-200 bg-brand-50/50 px-2 py-1.5 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
            title="Build a new marketing email from scratch in the drag-and-drop builder"
          >
            <Plus className="h-3.5 w-3.5" />
            New email
          </Link>
        )}
      </div>

      {/* Preview */}
      <div className="min-w-0 flex-1">
        {selected ? (
          <div className="rounded-xl border border-hairline bg-white shadow-e1">
            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">
                  {selected.name}
                </div>
                <div className="truncate text-[11px] text-slate-400">
                  {selected.subject ? `Subject: ${selected.subject}` : "No subject"} ·
                  v{selected.version}
                  {selected.updated_by ? ` · ${selected.updated_by.split(" (")[0]}` : ""}
                </div>
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-1.5">
                {selected.kind === "marketing" ? (
                  canManage && (
                    <Link
                      to={`/email-builder?template=${encodeURIComponent(selected.key)}`}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-800"
                      title="Open this template in the drag-and-drop email builder"
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Link>
                  )
                ) : (
                  <span
                    title="Statement and invoice templates have repeating data rows — code-managed for now"
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400"
                  >
                    Code-managed
                  </span>
                )}
                {canManage && (
                  <button
                    type="button"
                    disabled={setArchived.isPending}
                    onClick={() =>
                      setArchived.mutate({ id: selected.id, archived: !selected.archived })
                    }
                    title={
                      selected.archived
                        ? "Restore — usable for sends again"
                        : "Archive — hidden from send surfaces (never deleted)"
                    }
                    className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    {selected.archived ? (
                      <>
                        <ArchiveRestore className="h-3 w-3" /> Restore
                      </>
                    ) : (
                      <>
                        <Archive className="h-3 w-3" /> Archive
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {/* Emails are 640px wide; the Settings column isn't — scale the
                whole render to fit so the preview reads like the real thing
                instead of clipping at the right edge. */}
            <div className="overflow-hidden rounded-b-xl bg-slate-100" style={{ height: 560 }}>
              <iframe
                title={`Template preview — ${selected.name}`}
                srcDoc={selected.html}
                sandbox="allow-same-origin"
                style={{
                  width: 680,
                  height: 740,
                  border: 0,
                  transform: "scale(0.76)",
                  transformOrigin: "top left",
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-400">
            No templates yet.
          </div>
        )}
      </div>
    </div>
  );
}
